<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link
              class="navbar-brand"
              to="/"
            >
              <span class="brand-logo">
                <logo />
              </span>
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >

      <div
        v-if="hasAtLeastOneRole(me.roles, ['ROLE_DISPATCHER'])"
        class="mx-2 my-1"
      >
        <b-button
          v-if="!isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered)"
          variant="outline-primary"
          class="text-nowrap"
          pill
          block
          @click="addNewOrder"
        >
          <feather-icon
            icon="FilePlusIcon"
            class="mr-50"
          />
          {{ $t('shared.new_order_button') }}
        </b-button>
      </div>

      <current-order-button v-show="!isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered)" />

      <vertical-nav-menu-items
        :items="menuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import navMenuItems from '@/navigation/vertical'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink, BButton } from 'bootstrap-vue'
import {
  provide,
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
} from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import Logo from '@core-override/layouts/components/Logo.vue'
import CurrentOrderButton
  from '@core/layouts/layout-vertical/components/vertical-nav-menu/components/CurrentOrderButton.vue'
import PusherClient from '@/libs/pusher'
import guard from '@/guard'
import store from '@/store/store'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    CurrentOrderButton,
    Logo,
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BButton,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  methods: {
    hasAtLeastOneRole: guard.hasAtLeastOneRole,
    addNewOrder() {
      this.$router.push('/order/create')
    },
  },
  setup(props) {
    const dispatcherReviewRoles = ['ROLE_DISPATCHER']
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const me = computed(() => store.state.auth.activeUser)
    const meRolesForRealtime = computed(
      () => (Array.isArray(me.value?.rawRoles) && me.value.rawRoles.length > 0
        ? me.value.rawRoles
        : (me.value?.roles || [])),
    )
    const hasDispatcherReviewAccess = computed(
      () => guard.hasAtLeastOneRole(meRolesForRealtime.value, dispatcherReviewRoles),
    )
    const dispatcherReviewPendingTotal = computed(
      () => store.getters['externalOrderRequest/getDispatcherReviewPendingTotal'] || 0,
    )
    const refreshTimeoutId = ref(null)
    const retryTimeoutId = ref(null)
    const retryCount = ref(0)
    const pusherExternalOrderRequestHandler = ref(null)

    const fetchDispatcherReviewPendingTotal = () => {
      if (!hasDispatcherReviewAccess.value) {
        return
      }

      store.dispatch('externalOrderRequest/fetchDispatcherReviewPendingTotal')
        .then(() => {
          retryCount.value = 0
          if (retryTimeoutId.value) {
            clearTimeout(retryTimeoutId.value)
            retryTimeoutId.value = null
          }
        })
        .catch(() => {
          if (!hasDispatcherReviewAccess.value) {
            return
          }
          if (retryTimeoutId.value || retryCount.value >= 5) {
            return
          }

          retryTimeoutId.value = setTimeout(() => {
            retryTimeoutId.value = null
            retryCount.value += 1
            fetchDispatcherReviewPendingTotal()
          }, 1500)
        })
    }

    const schedulePendingBadgeRefresh = () => {
      if (refreshTimeoutId.value) {
        return
      }

      refreshTimeoutId.value = setTimeout(() => {
        refreshTimeoutId.value = null
        fetchDispatcherReviewPendingTotal()
      }, 350)
    }

    watch(hasDispatcherReviewAccess, hasAccess => {
      if (hasAccess) {
        fetchDispatcherReviewPendingTotal()
      }
    }, { immediate: true })

    onMounted(() => {
      if (pusherExternalOrderRequestHandler.value) {
        return
      }

      pusherExternalOrderRequestHandler.value = () => {
        schedulePendingBadgeRefresh()
      }

      PusherClient.bind('update', 'external_order_request', pusherExternalOrderRequestHandler.value)
    })

    onBeforeUnmount(() => {
      if (refreshTimeoutId.value) {
        clearTimeout(refreshTimeoutId.value)
        refreshTimeoutId.value = null
      }
      if (retryTimeoutId.value) {
        clearTimeout(retryTimeoutId.value)
        retryTimeoutId.value = null
      }

      if (pusherExternalOrderRequestHandler.value) {
        PusherClient.unbind('update', 'external_order_request', pusherExternalOrderRequestHandler.value)
        pusherExternalOrderRequestHandler.value = null
      }
    })

    const menuItems = computed(() => {
      const config = store.getters['appConfig/config']

      return navMenuItems.map(item => {
        const nextItem = { ...item }

        if (typeof item.conditionalShowMethod === 'function') {
          nextItem.hidden = !item.conditionalShowMethod(me.value, config)
        }

        if (typeof item.featureVisibilityMethod === 'function' && !item.featureVisibilityMethod(config)) {
          nextItem.hidden = true
        }

        if (item.route === 'dispatcher-order-request-review-list') {
          nextItem.tagRaw = dispatcherReviewPendingTotal.value > 0
            ? String(dispatcherReviewPendingTotal.value)
            : null
          nextItem.tagVariant = 'danger'
        }

        if (Array.isArray(item.children)) {
          nextItem.children = item.children.map(child => {
            const nextChild = { ...child }

            if (typeof child.conditionalShowMethod === 'function') {
              nextChild.hidden = !child.conditionalShowMethod(me.value, config)
            }

            if (typeof child.featureVisibilityMethod === 'function' && !child.featureVisibilityMethod(config)) {
              nextChild.hidden = true
            }

            return nextChild
          })
        }

        return nextItem
      })
    })

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    // App Name
    const { appName, appLogoImage } = $themeConfig.app

    return {
      menuItems,
      me,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";
</style>

<style lang="scss" scoped>
.navbar-header {
  &.expanded {
    min-height: 5.25rem;
  }
}
</style>
